/**
 * Platform-resources runtime client.
 *
 * Factory pattern: each consumer constructs a client once at its module
 * top, passing the CDN base URL read from its own env (Vite's
 * `loadEnv()` for `client/`, `process.env.PLATFORM_RESOURCES_CDN_DOMAIN`
 * for services, etc.). The package itself reads no env — that keeps the
 * SDK migration honest: external plugins inherit the host page's CDN
 * choice via a client passed to them, not via a URL baked at SDK build
 * time.
 *
 * No URL composition lives outside this file (and the sync script).
 * The architecture trip-wire at
 * `tests/architecture/platform-resources-url-composition.test.ts`
 * pins this.
 */

import manifest from './manifest.json';
import { CATALOG_VERSION, FONT_CATALOG, type CatalogFamily } from './catalog';
import type { PlatformResourcesManifest } from './manifest-schema';

// The manifest is generated by the sync script; before its first run
// the JSON is the empty-shaped placeholder shipped in the repo. Cast
// safely — `resolveSrc...` calls below treat missing entries as "this
// family isn't vendored yet."
const MANIFEST = manifest as PlatformResourcesManifest;

/**
 * A picker-friendly summary of a catalog entry. Used by the
 * image-editor font picker; the shape matches the legacy `FontEntry`
 * the picker already consumed so the migration is structural-only.
 */
export interface FontEntry {
  /** CSS `font-family` name; the value persisted by callers (e.g. the
   *  Fabric IText `fontFamily` property). */
  value: string;
  label: string;
  /** A `font-family:` fallback chain — picker rows render legibly even
   *  before the `@font-face` arrives. */
  preview: string;
  category: CatalogFamily['category'];
  /** `false` for OS fonts (Arial, Helvetica, …) — picker shouldn't
   *  attempt to fetch CSS for them. */
  vendored: boolean;
}

export interface PlatformResourcesClient {
  /**
   * URLs to per-family CSS files. Each CSS file contains all `@font-face`
   * declarations for that family's weights/styles. Returning URLs
   * (rather than inline CSS) means the caller controls how markup is
   * rendered — `<link>` tags in HTML, dynamic injection in JS,
   * `@import` in CSS, etc.
   *
   * Returns `[]` for families absent from the vendored manifest (e.g.
   * if called before the first sync runs), and for every family when the
   * client was constructed with an empty `baseUrl` (CDN unconfigured).
   */
  getFontCssUrls(families: string[]): string[];

  /**
   * Combined `@font-face` CSS text for the given families (default: the
   * whole vendored catalog), with absolute CDN `src` URLs. Unlike
   * {@link getFontCssUrls} this returns ready-to-inline CSS rather than
   * stylesheet URLs — the host inlines it into the plugin iframe's
   * `<head>` so every catalog family is registered with `document.fonts`
   * up front while the `.woff2` bytes stay lazy (a face only fetches when
   * a glyph in it is actually painted). That gives external plugins the
   * full font catalog with zero per-font network round-trips and no
   * dependency on this (private) package.
   *
   * Returns `''` when the CDN is unconfigured (empty `baseUrl`) or when no
   * requested family is present in the vendored manifest.
   */
  getFontFaceCss(families?: string[]): string;

  /** Catalog entries usable in pickers; system fonts included with
   *  `vendored: false` so the picker still offers them. */
  getAvailableFonts(): FontEntry[];

  /**
   * Lazy-inject the family's CSS into `document.head` and resolve once
   * the bytes have loaded. Honors a 3s timeout — the promise resolves
   * (does NOT reject) if the network is slow or the CDN is blocked, so
   * callers can proceed with the CSS fallback chain. Idempotent — a
   * second call for the same family is a no-op.
   *
   * No-ops for system fonts and for families absent from the manifest.
   */
  ensureFontLoaded(family: string): Promise<void>;
}

/**
 * Normalize a raw CDN domain (from env) into a `baseUrl` for
 * {@link createPlatformResourcesClient}. The single, shared resolution
 * every consumer (client app, plugin-build-service, plugin-builder-preview)
 * uses so the three surfaces can't drift:
 *
 *  - trims surrounding whitespace (CI secret plumbing often appends a
 *    trailing newline);
 *  - an empty/whitespace-only value returns `''` → the client treats the
 *    CDN as unconfigured and degrades to system fonts. NEVER defaults to a
 *    concrete CDN host, so staging/dev can't silently pull prod bytes;
 *  - a bare host (no scheme) is coerced to `https://`.
 */
export function normalizeCdnBaseUrl(raw: string | undefined | null): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';
  return trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
}

export interface CreatePlatformResourcesClientOptions {
  /** CDN base URL without trailing slash (e.g.
   *  `https://cdn.platform.fias.io`). An empty string means the CDN is
   *  unconfigured for this environment: the client emits no font URLs and
   *  marks every family unvendored, so consumers degrade to system fonts. */
  baseUrl: string;
}

/**
 * Factory. Construct one per consumer at module top.
 */
export function createPlatformResourcesClient(
  opts: CreatePlatformResourcesClientOptions,
): PlatformResourcesClient {
  const baseUrl = opts.baseUrl.replace(/\/$/, '');
  // An empty baseUrl means the CDN domain isn't configured for this
  // environment (a deploy before PlatformResourcesCdnStack is live, or
  // local dev without the domain plumbed). Treat it as "unconfigured":
  // emit no font URLs and mark every family unvendored, so consumers fall
  // back to system fonts with zero failed requests — rather than composing
  // a broken `https://fonts/...` host or an app-origin-relative URL.
  const configured = baseUrl.length > 0;
  const prefix = `${baseUrl}/fonts/${CATALOG_VERSION}`;

  const vendoredById = new Map<string, CatalogFamily>();
  for (const family of FONT_CATALOG) {
    if (!family.system) vendoredById.set(family.id, family);
  }

  // The manifest tells us *which* families have actually been vendored
  // (i.e. survived a sync). The catalog tells us what *should* be
  // vendored. Until the first sync runs the manifest's `families` is
  // empty and `getFontCssUrls` returns `[]` for everything — that's
  // correct: no CSS exists at the CDN yet, so emitting URLs to it would
  // produce 404s.
  const manifestFamilyIds = new Set<string>();
  for (const f of MANIFEST.families) manifestFamilyIds.add(f.id);

  const familyIdByName = new Map<string, string>();
  for (const family of FONT_CATALOG) {
    familyIdByName.set(family.family, family.id);
  }

  // For @font-face composition: family id → CSS name, and file id → file.
  const familyNameById = new Map<string, string>();
  for (const f of MANIFEST.families) familyNameById.set(f.id, f.family);
  const fileById = new Map<string, (typeof MANIFEST.files)[number]>();
  for (const file of MANIFEST.files) fileById.set(file.id, file);

  function cssUrlFor(familyName: string): string | null {
    if (!configured) return null;
    const id = familyIdByName.get(familyName);
    if (!id) return null;
    if (!manifestFamilyIds.has(id)) return null;
    return `${prefix}/${id}/index.css`;
  }

  const loadingByFamily = new Map<string, Promise<void>>();

  return {
    getFontCssUrls(families) {
      const urls: string[] = [];
      for (const familyName of families) {
        const url = cssUrlFor(familyName);
        if (url) urls.push(url);
      }
      return urls;
    },

    getFontFaceCss(families) {
      if (!configured) return '';
      // `families` filters by CSS family NAME; undefined = whole catalog.
      const wanted = families ? new Set(families) : null;
      const blocks: string[] = [];
      for (const face of MANIFEST.faces) {
        const familyName = familyNameById.get(face.familyId);
        if (!familyName) continue;
        if (wanted && !wanted.has(familyName)) continue;
        const file = fileById.get(face.fileId);
        if (!file) continue;
        // Variable-axis faces carry a [min, max] range → `font-weight: 100 900`.
        const weight = Array.isArray(face.weight)
          ? `${face.weight[0]} ${face.weight[1]}`
          : `${face.weight}`;
        blocks.push(
          `@font-face{` +
            `font-family:"${familyName}";` +
            `font-style:${face.style};` +
            `font-weight:${weight};` +
            // `swap` keeps DOM text legible with the fallback until bytes
            // arrive; canvas callers gate on `document.fonts.load` so it
            // doesn't affect them.
            `font-display:swap;` +
            `src:url(${prefix}/${file.relPath}) format("woff2");` +
            `unicode-range:${face.unicodeRange}` +
            `}`,
        );
      }
      return blocks.join('\n');
    },

    getAvailableFonts() {
      return FONT_CATALOG.map((f) => ({
        value: f.family,
        label: f.displayName,
        preview: buildPreviewFallbackChain(f),
        category: f.category,
        vendored: configured && !f.system && manifestFamilyIds.has(f.id),
      }));
    },

    ensureFontLoaded(family) {
      // System fonts and unvendored families short-circuit.
      const url = cssUrlFor(family);
      if (!url) return Promise.resolve();
      if (typeof document === 'undefined') return Promise.resolve();

      const existing = loadingByFamily.get(family);
      if (existing) return existing;

      const promise = injectAndAwait(url, family);
      loadingByFamily.set(family, promise);
      return promise;
    },
  };
}

/**
 * Build a CSS `font-family:` fallback chain so picker rows render
 * legibly before the `@font-face` arrives. Matches the historical
 * image-editor preview shape.
 */
function buildPreviewFallbackChain(f: CatalogFamily): string {
  const generic = ((): string => {
    switch (f.category) {
      case 'sans':
        return 'sans-serif';
      case 'serif':
        return 'serif';
      case 'mono':
        return 'monospace';
      case 'display':
        return 'sans-serif';
      case 'handwriting':
        return 'cursive';
      case 'system':
        // System fonts have their own historical fallback chains in
        // the image-editor picker; the helper there overrides this.
        return 'sans-serif';
    }
  })();
  const quoted = f.family.includes(' ') ? `"${f.family}"` : f.family;
  return `${quoted}, ${generic}`;
}

/**
 * Inject a `<link rel="stylesheet">` and resolve once the font is
 * ready to render. Resolves (does NOT reject) on 3s timeout so callers
 * always make forward progress — offline environments block the
 * stylesheet fetch silently, and a bounded resolution lets the picker
 * continue showing the family with its fallback chain.
 *
 * Two-step ordering is load-bearing (the subtle bug the image-editor's
 * pre-migration `google-fonts.ts` discovered and documented):
 *
 *   STEP 1 — wait for the `<link>` to finish fetching. Without this,
 *            the `@font-face` declarations may not yet be registered
 *            with `document.fonts`, and a premature `document.fonts.load`
 *            resolves immediately with `[]` (no FontFace entries match
 *            the family). The caller then thinks the font is ready and
 *            measures glyph metrics against the fallback — exactly the
 *            "saved fonts load, IText paints in the wrong face" bug.
 *
 *   STEP 2 — once `@font-face` is registered, `document.fonts.load`
 *            resolves with the matched FontFace entries when their
 *            byte fetch completes.
 *
 * Both steps race against a single 3s timeout. The timeout NEVER
 * rejects; callers can `await` without `try`/`catch`.
 *
 * The 3-second timeout + `document.fonts.load` race semantics are
 * shared with the image-editor's `lib/fonts.ts` `whenFontReady`. Keep
 * the two implementations aligned — the 2026-05 institutional
 * knowledge is non-obvious enough that a copy-paste divergence would
 * silently regress the canvas font-metrics bug.
 */
function injectAndAwait(url: string, family: string): Promise<void> {
  return new Promise<void>((resolve) => {
    // Re-use an existing link if present (other callers may have
    // injected this URL already); otherwise create one.
    const findLink = (): HTMLLinkElement | null =>
      Array.from(document.head.querySelectorAll('link')).find(
        (l) => l.getAttribute('href') === url,
      ) ?? null;

    let link = findLink();
    const linkAlreadyInDom = link !== null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.setAttribute('data-platform-resources-family', family);
      document.head.appendChild(link);
    }

    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeout);
      resolve();
    };
    const timeout = setTimeout(finish, 3000);

    const fontsApi = (document as unknown as { fonts?: FontFaceSet }).fonts;

    // Wait for the stylesheet to finish fetching first so the
    // `@font-face` is registered with document.fonts.
    const linkLoaded = new Promise<void>((resolveLink) => {
      // If the link was already in the DOM, treat it as loaded — the
      // earlier injector handled the await, and a re-add would only
      // duplicate work.
      if (linkAlreadyInDom) {
        resolveLink();
        return;
      }
      link!.addEventListener('load', () => resolveLink(), { once: true });
      link!.addEventListener('error', () => resolveLink(), { once: true });
    });

    void linkLoaded.then(async () => {
      if (resolved) return;
      // Now that @font-face is registered, ask for the byte fetch.
      if (fontsApi && typeof fontsApi.load === 'function') {
        try {
          await fontsApi.load(`12px "${family}"`);
        } catch {
          // Silent — fallback chain keeps rendering.
        }
      }
      finish();
    });
  });
}
